import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuthStore } from "@/stores/authStore";
import { useToast } from "@/hooks/use-toast";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { getApiError } from "@/services/api";
import api from "@/services/api";
import { getOrCreateDeviceId } from "@/utils/deviceId";
import { billingService } from "@/services/billingService";
import CompanySelector from "@/components/auth/CompanySelector";

const DEFAULT_EMAIL_DOMAIN = "@i-one.co.kr";
const SAVED_EMAIL_KEY = "hubcore_last_email";

interface CompanyInfo {
  user_id: string;
  company_id: string;
  company_name: string;
  company_code: string;
  company_logo_url: string | null;
  role: string;
  user_type: string;
  is_active: boolean;
  last_login_at: string | null;
}

export default function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [emailInput, setEmailInput] = useState(""); // 이메일 입력값 별도 관리
  const [passwordInput, setPasswordInput] = useState(""); // 비밀번호 입력값 별도 관리
  const [showCompanySelector, setShowCompanySelector] = useState(false);
  const [companies, setCompanies] = useState<CompanyInfo[]>([]);
  const { login, isLoading } = useAuthStore();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { canInstall, promptInstall } = usePWAInstall();

  // 저장된 이메일 불러오기
  useEffect(() => {
    const savedEmail = localStorage.getItem(SAVED_EMAIL_KEY);
    if (savedEmail) {
      setEmailInput(savedEmail);
    }
  }, []);

  // 이메일 변환: @가 없으면 기본 도메인 추가
  const getLoginEmail = (input: string): string => {
    const trimmed = input.trim();
    if (!trimmed.includes("@")) {
      return trimmed + DEFAULT_EMAIL_DOMAIN;
    }
    return trimmed;
  };

  // 기업 목록 조회
  const checkCompanies = async () => {
    const loginEmail = getLoginEmail(emailInput);

    try {
      const response = await api.post("/auth/check-companies", {
        email: loginEmail,
        password: passwordInput,
        device_id: getOrCreateDeviceId(),
      });

      const data = response.data;

      // 기업이 여러 개면 선택 UI 표시
      if (data.has_multiple_companies) {
        setCompanies(data.companies);
        setShowCompanySelector(true);
        return false;
      }

      // 기업이 1개면 바로 로그인
      return await handleLogin();
    } catch (error) {
      const apiError = getApiError(error);

      if (apiError.code === "DEVICE_BLOCKED" || apiError.code === "MAX_DEVICES_REACHED") {
        alert(apiError.message);
      } else {
        toast({
          title: "로그인 실패",
          description: apiError.message,
          variant: "destructive",
          duration: 5000,
        });
      }
      return false;
    }
  };

  // 기업를 선택한 후 로그인
  const handleCompanySelect = async (companyCode: string) => {
    return await handleLogin(companyCode);
  };

  const handleLogin = async (companyCode?: string, e?: React.FormEvent | React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const loginEmail = getLoginEmail(emailInput);

    try {
      await login({
        email: loginEmail,
        password: passwordInput,
        company_code: companyCode, // 기업 코드 전달
      });

      // 로그인 성공 시 이메일 저장
      localStorage.setItem(SAVED_EMAIL_KEY, emailInput.trim());

      // 구독이 잠긴 회사는 메뉴로 보내지 않고 바로 잠금 화면으로.
      // (메뉴에 진입하면 첫 API가 403 SUBSCRIPTION_LOCKED → api.ts가 강제 새로고침으로 /billing/locked 이동하는 우회 흐름을 피함)
      const loggedInUser = useAuthStore.getState().user;
      if (loggedInUser && loggedInUser.role !== "super_admin") {
        const billing = await billingService.getStatus().catch(() => null);
        if (billing?.enforcement_enabled && billing.status === "locked") {
          navigate("/billing/locked", { replace: true });
          return true;
        }
      }

      // 권한 그룹이 없는 경우 리다이렉트 대신 App.tsx의 ProtectedRoute가 처리하도록 함
      const { noPermissionGroup, passwordChangeRequired, onboardingRequired, noCompany } = useAuthStore.getState();

      // 회사 미소속(로비) 사용자는 user가 null이라 PublicRoute가 자동 리다이렉트하지 못하므로
      // 명시적으로 로비(/no-company)로 이동시킨다.
      if (noCompany) {
        navigate("/no-company", { replace: true });
      } else if (!noPermissionGroup && !passwordChangeRequired && !onboardingRequired) {
        // 그 외 특별 상태는 ProtectedRoute가 처리. 정상 사용자는 첫 메뉴로 이동.
        const defaultPath = useAuthStore.getState().getDefaultLandingPath();
        navigate(defaultPath);
      }

      toast({
        title: "로그인 성공",
        description: "환영합니다!",
      });

      // PWA 설치 프롬프트 (앱 미설치 시)
      if (canInstall) {
        setTimeout(() => promptInstall(), 1000);
      }

      return true;
    } catch (error) {
      const apiError = getApiError(error);

      // 에러 코드별 메시지 처리
      let errorTitle = "로그인 실패";
      let errorMessage = apiError.message;

      if (apiError.code === "APPROVAL_PENDING") {
        errorTitle = "승인 대기 중";
        errorMessage = "관리자 승인 대기 중입니다. 승인이 완료되면 이메일로 알림을 보내드립니다.";
      } else if (apiError.code === "APPROVAL_REJECTED") {
        errorTitle = "승인 거부됨";
        errorMessage = apiError.message; // 백엔드에서 거부 사유 포함
      } else if (apiError.code === "INVALID_CREDENTIALS") {
        errorTitle = "로그인 실패";
        errorMessage = "이메일 또는 비밀번호가 일치하지 않습니다.";
      } else if (apiError.code === "MAX_DEVICES_REACHED") {
        alert(apiError.message);
        return false;
      } else if (apiError.code === "DEVICE_BLOCKED") {
        alert(apiError.message);
        return false;
      } else if (apiError.code === "DEVICE_ID_REQUIRED") {
        errorTitle = "기기 인식 실패";
        errorMessage = "기기를 인식할 수 없습니다. 브라우저의 쿠키/저장소를 초기화한 후 다시 시도해주세요.";
      }

      toast({
        title: errorTitle,
        description: errorMessage,
        variant: "destructive",
        duration: 5000,
      });
      return false;
    }
  };

  const handleSubmit = async (e?: React.FormEvent | React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!emailInput.trim()) {
      toast({
        title: "이메일 또는 아이디를 입력해주세요.",
        variant: "destructive",
      });
      return false;
    }

    if (!passwordInput) {
      toast({
        title: "비밀번호를 입력해주세요.",
        variant: "destructive",
      });
      return false;
    }

    // 기업 목록 조회 후 로그인 처리
    return await checkCompanies();
  };

  return (
    <div>
      {showCompanySelector ? (
        <CompanySelector
          companies={companies}
          email={getLoginEmail(emailInput)}
          onSelect={handleCompanySelect}
          onCancel={() => setShowCompanySelector(false)}
        />
      ) : (
        <>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">이메일</Label>
              <Input
                id="email"
                type="text"
                placeholder="name@example.com"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">비밀번호</Label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-primary hover:underline"
                >
                  비밀번호 찾기
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={passwordInput}
                  onChange={(e) => setPasswordInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSubmit();
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            <Button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSubmit(e);
              }}
              className="w-full"
              disabled={isLoading}
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              로그인
            </Button>
          </div>

          <div className="mt-6 text-center text-sm">
            <span className="text-muted-foreground">처음이신가요? </span>
            <Link to="/register" className="text-primary hover:underline font-semibold">
              회원가입
            </Link>
          </div>

          <div className="mt-4 text-center text-sm border-t pt-4">
            <span className="text-muted-foreground">회사를 운영하시나요? </span>
            <Link to="/company-register" className="text-primary hover:underline">
              새 회사 만들기 (기업 회원가입)
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
